import {
  ButtonBase,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import formatBytes from 'bytes';
import { formatDistanceToNowStrict } from 'date-fns';

import httpInstances from '@/api/axiosInstance';
import DownloadFile from '@/assets/icons/DownloadFile';
import EllipsisWithTooltip from '@/components/EllipsisWithTooltip';
import downloadFile from '@/utils/downloadFile';

import { EmptyState } from '../EmptyState';
import Loader from '../Loader';

interface WidgetFile {
  name: string;
  size: string;
  created: string;
  fid: string;
}

interface FilesResponse {
  totalSize: number;
  items: {
    fileName: string;
    fileType: string;
    groupName: string;
    fid: string;
    latest: boolean;
    size: number;
    arrivalTime: string;
    fields?: {
      createdAt?: string;
    };
  }[];
}

const { axiosInternalInstance: instance } = httpInstances;

const parseData = (data: FilesResponse): WidgetFile[] => {
  return data.items.map(item => ({
    name: item.fileName,
    size:
      formatBytes(item.size, {
        unitSeparator: ' '
      }) ?? '-',
    created: formatDistanceToNowStrict(new Date(item.fields?.createdAt ?? item.arrivalTime), {
      addSuffix: true
    }),
    fid: item.fid
  }));
};

const FilesWidget = ({
  groupName,
  size,
  downloadable
}: {
  groupName: string | undefined;
  size?: number;
  downloadable?: string;
}) => {
  const theme = useTheme();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['widget-files', size, groupName],
    queryFn: async () => {
      const sizeParam = size ? `&size=${size}` : '';
      const url = `/proxy/datalake/search?fileName=*&groupName=${encodeURIComponent(groupName ?? '')}${sizeParam}`;
      const response = await instance.get(url);
      return parseData(response.data);
    },
    enabled: !!groupName
  });
  if (isLoading) return <Loader />;
  if (isError || !groupName) return <EmptyState />;
  return (
    <Paper>
      <TableContainer
        sx={{
          borderCollapse: 'separate',
          borderSpacing: 0
        }}
      >
        <Table
          stickyHeader
          size='small'
          sx={{
            width: '100%',
            tableLayout: 'fixed'
          }}
        >
          <TableHead
            sx={{
              '& th': {
                px: 3,
                pb: 1,
                borderBottom: `0.5px solid ${theme.palette.text.primary}`,
                backgroundColor: 'background.paper'
              }
            }}
          >
            <TableRow>
              <TableCell sx={{ width: 'auto' }}>File name</TableCell>
              <TableCell sx={{ width: 120 }}>Size</TableCell>
              <TableCell sx={{ width: 120 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.map(file => (
              <TableRow
                key={file.name}
                sx={{
                  height: '30px',
                  '&:nth-of-type(even)': {
                    backgroundColor: '#121836'
                  },
                  '& td': {
                    px: 3
                  }
                }}
              >
                <TableCell
                  sx={{
                    overflow: 'hidden'
                  }}
                >
                  <EllipsisWithTooltip text={file.name} />
                </TableCell>
                <TableCell>{file.size}</TableCell>
                <TableCell>
                  <ButtonBase
                    disableRipple
                    disabled={downloadable === 'false'}
                    sx={{
                      color: theme.palette.primary.main
                    }}
                    onClick={() => {
                      void downloadFile(file.fid, file.name);
                    }}
                  >
                    <DownloadFile
                      sx={{
                        mr: '4px'
                      }}
                    />
                    Download
                  </ButtonBase>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default FilesWidget;
